import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  bureauQueries,
  integrationCalls,
  type BureauQueryType,
  type BureauQueryTypeInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { nomeDoProvedor } from '@/shared/lib/nomeDoProvedor';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { HistoryTable } from '@/shared/ui/HistoryTab';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

import { Trilha } from './Trilha';

/**
 * Bureaus por tipo de consulta (`/parametrizacao/bureaus-consulta` e
 * `/:queryTypeId`), as telas do OnPoint que o inventario nao chegou a abrir (PAR-147,
 * PAR-148). As tabelas e as rotas sao do registro de integracoes (P-INT, ADR 0024);
 * a tela e da parametrizacao.
 *
 * A prioridade ainda nao escolhe o bureau: a consulta de credito continua no provedor
 * da plataforma (ADR 0017). A ordem fica registrada para quando houver mais de um
 * bureau com contrato.
 */

const trilhaDosBureaus = [
  { rotulo: 'Parametrizacao', para: '/parametrizacao' },
  { rotulo: 'Consultas PJ e Pre-validacao', para: '/parametrizacao/consulta-pj' },
];

const rotulosDaAcao: Record<string, string> = {
  'integracoes.consulta_bureau.create': 'Tipo de consulta cadastrado',
  'integracoes.consulta_bureau.update': 'Tipo de consulta alterado',
  'integracoes.consulta_bureau.delete': 'Tipo de consulta excluido',
};

function useAcessoAosBureaus() {
  const { can, hasFeature } = useAuth();
  const funcionalidade = hasFeature('administracao.integracoes');
  return {
    podeLer: funcionalidade && can('integracoes.integracao.view'),
    podeEditar: funcionalidade && can('integracoes.integracao.update'),
  };
}

/** Tipos de chamada da familia bureau, para o tipo de consulta. */
function useTiposDeChamada(habilitado: boolean) {
  const tipos = useQuery({
    queryKey: ['integration-call-types'],
    queryFn: ({ signal }) => integrationCalls.types(signal),
    enabled: habilitado,
  });
  return (tipos.data ?? [])
    .filter((tipo) => tipo.kind === 'bureau')
    .map((tipo) => ({ value: tipo.key, label: tipo.name }));
}

function SemAcesso() {
  return (
    <Alert tone="info" title="Sem acesso">
      A parametrizacao dos bureaus exige a leitura das integracoes
      (integracoes.integracao.view) e a funcionalidade de integracoes.
    </Alert>
  );
}

/** Dialogo "Novo tipo de consulta". */
function NovoTipo({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tiposDeChamada = useTiposDeChamada(true);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipoDeChamada, setTipoDeChamada] = useState('');

  const criar = useMutation({
    mutationFn: () =>
      bureauQueries.create({
        name: nome,
        description: descricao,
        call_type: tipoDeChamada,
      }),
    onSuccess: (criado) => {
      toast.success(`Tipo de consulta ${criado.name} cadastrado.`);
      void queryClient.invalidateQueries({ queryKey: ['bureau-queries'] });
      onClose();
      void navigate(`/parametrizacao/bureaus-consulta/${criado.id}`);
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    criar.mutate();
  }

  return (
    <Modal open onClose={onClose} title="Novo tipo de consulta">
      <form onSubmit={enviar} className="flex flex-col gap-4">
        {criar.error ? (
          <Alert tone="danger" title="Nao foi possivel cadastrar">
            {criar.error.message}
          </Alert>
        ) : null}
        <Field
          label="Nome do tipo de consulta"
          required
          maxLength={120}
          value={nome}
          onChange={(evento) => setNome(evento.target.value)}
        />
        <Field
          label="Descricao do tipo de consulta"
          value={descricao}
          onChange={(evento) => setDescricao(evento.target.value)}
        />
        <Select
          label="Tipo de chamada"
          required
          placeholder="Escolha o tipo de chamada"
          value={tipoDeChamada}
          options={tiposDeChamada}
          onChange={(evento) => setTipoDeChamada(evento.target.value)}
        />
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={criar.isPending}>
            Cadastrar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function HistoricoDosBureaus({ onClose }: { onClose: () => void }) {
  const historico = useQuery({
    queryKey: ['bureau-queries', 'history'],
    queryFn: ({ signal }) => bureauQueries.history(signal),
  });
  return (
    <Modal open onClose={onClose} title="Historico de alteracoes" size="lg">
      {historico.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : historico.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar o historico">
          {historico.error.message}
        </Alert>
      ) : (
        <HistoryTable
          rows={historico.data}
          emptyText="Nenhuma alteracao registrada"
          actionLabel={(acao) => rotulosDaAcao[acao] ?? acao}
        />
      )}
    </Modal>
  );
}

/** Lista dos tipos de consulta, com os bureaus em ordem de prioridade. */
export function BureauQueriesPage() {
  const { podeLer, podeEditar } = useAcessoAosBureaus();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [criando, setCriando] = useState(false);
  const [vendoHistorico, setVendoHistorico] = useState(false);
  const [excluindo, setExcluindo] = useState<BureauQueryType | null>(null);

  const lista = useQuery({
    queryKey: ['bureau-queries'],
    queryFn: ({ signal }) => bureauQueries.list(signal),
    enabled: podeLer,
  });
  const tipos = useQuery({
    queryKey: ['integration-call-types'],
    queryFn: ({ signal }) => integrationCalls.types(signal),
    enabled: podeLer,
  });
  const nomeDoTipo = new Map((tipos.data ?? []).map((tipo) => [tipo.key, tipo.name]));

  const excluir = useMutation({
    mutationFn: (queryTypeId: string) => bureauQueries.remove(queryTypeId),
    onSuccess: () => {
      toast.success('Tipo de consulta excluido.');
      setExcluindo(null);
      void queryClient.invalidateQueries({ queryKey: ['bureau-queries'] });
    },
    onError: (erro: Error) => {
      toast.error(erro.message);
      setExcluindo(null);
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <Trilha itens={trilhaDosBureaus} atual="Bureaus por tipo de consulta" />
      <PageHeader
        title="Bureaus por tipo de consulta"
        description="Os bureaus de cada tipo de consulta, em ordem de prioridade."
        actions={
          podeLer ? (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setVendoHistorico(true)}
              >
                Historico de alteracoes
              </Button>
              {podeEditar ? (
                <Button size="sm" onClick={() => setCriando(true)}>
                  Novo tipo de consulta
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />
      {!podeLer ? (
        <SemAcesso />
      ) : lista.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os tipos de consulta">
          {lista.error.message}
        </Alert>
      ) : (
        <Card>
          <CardBody className="p-0">
            <DataTable
              loading={lista.isPending}
              items={lista.data ?? []}
              keyOf={(item) => item.id}
              empty={
                <EmptyState
                  icon="target"
                  title="Nenhum tipo de consulta"
                  description="Cadastre um tipo de consulta para escolher os bureaus dele."
                />
              }
              columns={[
                {
                  key: 'nome',
                  header: 'Tipo de consulta',
                  render: (item) => (
                    <Link
                      to={`/parametrizacao/bureaus-consulta/${item.id}`}
                      className="font-medium text-brand-700 hover:underline"
                    >
                      {item.name}
                    </Link>
                  ),
                },
                {
                  key: 'chamada',
                  header: 'Tipo de chamada',
                  render: (item) => nomeDoTipo.get(item.call_type) ?? item.call_type,
                },
                {
                  key: 'bureaus',
                  header: 'Bureaus por prioridade',
                  render: (item) =>
                    item.bureaus.length === 0
                      ? 'Nenhum'
                      : [...item.bureaus]
                          .sort((a, b) => a.priority - b.priority)
                          .map(
                            (b) =>
                              `${b.priority}. ${nomeDoProvedor(b.provider_key, b.provider_name)}`,
                          )
                          .join(', '),
                },
                {
                  key: 'situacao',
                  header: 'Situacao',
                  render: (item) => (
                    <Badge tone={item.active ? 'success' : 'neutral'}>
                      {item.active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  ),
                },
                {
                  key: 'acoes',
                  header: 'Acoes',
                  render: (item) =>
                    podeEditar ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Excluir ${item.name}`}
                        onClick={() => setExcluindo(item)}
                      >
                        Excluir
                      </Button>
                    ) : null,
                },
              ]}
            />
          </CardBody>
        </Card>
      )}
      {criando ? <NovoTipo onClose={() => setCriando(false)} /> : null}
      {vendoHistorico ? (
        <HistoricoDosBureaus onClose={() => setVendoHistorico(false)} />
      ) : null}
      <ConfirmDialog
        open={excluindo !== null}
        onClose={() => setExcluindo(null)}
        onConfirm={() => excluindo && excluir.mutate(excluindo.id)}
        title="Excluir tipo de consulta"
        message={
          excluindo
            ? `O tipo ${excluindo.name} e os bureaus dele deixam de existir. A exclusao fica no historico.`
            : ''
        }
        confirmLabel="Excluir"
        tone="danger"
        loading={excluir.isPending}
      />
    </div>
  );
}

interface LinhaDeBureau {
  chave: number;
  provider_key: string;
  priority: string;
  active: boolean;
  notes: string;
}

/** Formulario do tipo de consulta, com a lista inteira de bureaus. */
function FormularioDoTipo({
  tipo,
  podeEditar,
}: {
  tipo: BureauQueryType;
  podeEditar: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const tiposDeChamada = useTiposDeChamada(true);
  const catalogo = useQuery({
    queryKey: ['bureaus'],
    queryFn: ({ signal }) => integrationCalls.bureaus(signal),
  });
  const opcoesDeBureau = (catalogo.data ?? [])
    .filter(
      (item) =>
        item.in_catalog &&
        (item.category === 'Credito' || item.category === 'Cadastral'),
    )
    .map((item) => ({ value: item.key, label: nomeDoProvedor(item.key, item.name) }));

  const [nome, setNome] = useState(tipo.name);
  const [descricao, setDescricao] = useState(tipo.description);
  const [tipoDeChamada, setTipoDeChamada] = useState(tipo.call_type);
  const [ativo, setAtivo] = useState(tipo.active);
  const [ordem, setOrdem] = useState(String(tipo.sort_order));
  const [linhas, setLinhas] = useState<LinhaDeBureau[]>(
    [...tipo.bureaus]
      .sort((a, b) => a.priority - b.priority)
      .map((b, indice) => ({
        chave: indice,
        provider_key: b.provider_key,
        priority: String(b.priority),
        active: b.active,
        notes: b.notes,
      })),
  );

  const salvar = useMutation({
    mutationFn: () => {
      const corpo: BureauQueryTypeInput = {
        name: nome,
        description: descricao,
        call_type: tipoDeChamada,
        active: ativo,
        sort_order: Number.parseInt(ordem, 10) || 0,
        bureaus: linhas.map((linha) => ({
          provider_key: linha.provider_key,
          priority: Number.parseInt(linha.priority, 10) || 0,
          active: linha.active,
          notes: linha.notes,
        })),
      };
      return bureauQueries.update(tipo.id, corpo);
    },
    onSuccess: () => {
      toast.success('Tipo de consulta salvo.');
      void queryClient.invalidateQueries({ queryKey: ['bureau-queries'] });
    },
  });

  function mudarLinha(chave: number, parte: Partial<LinhaDeBureau>) {
    setLinhas((atuais) =>
      atuais.map((linha) => (linha.chave === chave ? { ...linha, ...parte } : linha)),
    );
  }

  function acrescentar() {
    setLinhas((atuais) => [
      ...atuais,
      {
        chave: Math.max(-1, ...atuais.map((linha) => linha.chave)) + 1,
        provider_key: '',
        priority: String(atuais.length + 1),
        active: true,
        notes: '',
      },
    ]);
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  return (
    <form onSubmit={enviar} className="flex flex-col gap-4">
      {salvar.error ? (
        <Alert tone="danger" title="Nao foi possivel salvar o tipo de consulta">
          {salvar.error.message}
        </Alert>
      ) : null}
      <Card>
        <CardHeader title="Dados do tipo de consulta" />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Nome do tipo de consulta"
            required
            maxLength={120}
            disabled={!podeEditar}
            value={nome}
            onChange={(evento) => setNome(evento.target.value)}
          />
          <Select
            label="Tipo de chamada"
            required
            disabled={!podeEditar}
            value={tipoDeChamada}
            options={
              tiposDeChamada.some((opcao) => opcao.value === tipoDeChamada)
                ? tiposDeChamada
                : [...tiposDeChamada, { value: tipoDeChamada, label: tipoDeChamada }]
            }
            onChange={(evento) => setTipoDeChamada(evento.target.value)}
          />
          <Field
            label="Descricao do tipo de consulta"
            disabled={!podeEditar}
            value={descricao}
            onChange={(evento) => setDescricao(evento.target.value)}
          />
          <Field
            label="Ordem de exibicao"
            type="number"
            min={0}
            max={10000}
            disabled={!podeEditar}
            value={ordem}
            onChange={(evento) => setOrdem(evento.target.value)}
          />
          <label className="flex items-center gap-2 text-sm text-ink-800">
            <input
              type="checkbox"
              className="size-4 accent-[var(--color-brand-500)]"
              checked={ativo}
              disabled={!podeEditar}
              onChange={(evento) => setAtivo(evento.target.checked)}
            />
            Tipo de consulta ativo
          </label>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Bureaus"
          description="A prioridade 1 e a primeira. A ordem ainda nao escolhe o bureau consultado."
        />
        <CardBody className="flex flex-col gap-3">
          {linhas.length === 0 ? (
            <p className="text-sm text-ink-500">
              Nenhum bureau neste tipo de consulta.
            </p>
          ) : (
            linhas.map((linha, indice) => (
              <fieldset
                key={linha.chave}
                className="grid gap-3 rounded-md border border-line p-3 sm:grid-cols-4"
              >
                <legend className="sr-only">Bureau {indice + 1}</legend>
                <Select
                  label="Bureau"
                  placeholder="Escolha o bureau"
                  disabled={!podeEditar}
                  value={linha.provider_key}
                  options={
                    linha.provider_key === '' ||
                    opcoesDeBureau.some((opcao) => opcao.value === linha.provider_key)
                      ? opcoesDeBureau
                      : [
                          ...opcoesDeBureau,
                          {
                            value: linha.provider_key,
                            label: nomeDoProvedor(linha.provider_key),
                          },
                        ]
                  }
                  onChange={(evento) =>
                    mudarLinha(linha.chave, { provider_key: evento.target.value })
                  }
                />
                <Field
                  label="Prioridade"
                  type="number"
                  min={1}
                  max={99}
                  disabled={!podeEditar}
                  value={linha.priority}
                  onChange={(evento) =>
                    mudarLinha(linha.chave, { priority: evento.target.value })
                  }
                />
                <Field
                  label="Observacao do bureau"
                  disabled={!podeEditar}
                  value={linha.notes}
                  onChange={(evento) =>
                    mudarLinha(linha.chave, { notes: evento.target.value })
                  }
                />
                <div className="flex items-end justify-between gap-2">
                  <label className="flex items-center gap-2 text-sm text-ink-800">
                    <input
                      type="checkbox"
                      className="size-4 accent-[var(--color-brand-500)]"
                      checked={linha.active}
                      disabled={!podeEditar}
                      onChange={(evento) =>
                        mudarLinha(linha.chave, { active: evento.target.checked })
                      }
                    />
                    Ativo
                  </label>
                  {podeEditar ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`Retirar o bureau ${indice + 1}`}
                      onClick={() =>
                        setLinhas((atuais) =>
                          atuais.filter((item) => item.chave !== linha.chave),
                        )
                      }
                    >
                      Retirar
                    </Button>
                  ) : null}
                </div>
              </fieldset>
            ))
          )}
          {podeEditar ? (
            <div className="flex justify-between gap-2">
              <Button type="button" variant="secondary" onClick={acrescentar}>
                Adicionar bureau
              </Button>
              <Button type="submit" loading={salvar.isPending}>
                Salvar tipo de consulta
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </form>
  );
}

/** Tipo de consulta com os bureaus dele. */
export function BureauQueryDetailPage() {
  const { queryTypeId = '' } = useParams();
  const { podeLer, podeEditar } = useAcessoAosBureaus();

  const lista = useQuery({
    queryKey: ['bureau-queries'],
    queryFn: ({ signal }) => bureauQueries.list(signal),
    enabled: podeLer,
  });
  const tipo = (lista.data ?? []).find((item) => item.id === queryTypeId);

  return (
    <div className="flex flex-col gap-4">
      <Trilha
        itens={[
          ...trilhaDosBureaus,
          {
            rotulo: 'Bureaus por tipo de consulta',
            para: '/parametrizacao/bureaus-consulta',
          },
        ]}
        atual={tipo?.name ?? 'Tipo de consulta'}
      />
      <PageHeader
        title={tipo?.name ?? 'Tipo de consulta'}
        description="Dados do tipo de consulta e os bureaus dele, em ordem de prioridade."
      />
      {!podeLer ? (
        <SemAcesso />
      ) : lista.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : lista.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar o tipo de consulta">
          {lista.error.message}
        </Alert>
      ) : tipo ? (
        <FormularioDoTipo key={tipo.updated_at} tipo={tipo} podeEditar={podeEditar} />
      ) : (
        <Alert tone="warning" title="Tipo de consulta nao encontrado">
          O tipo de consulta nao existe nesta empresa.{' '}
          <Link to="/parametrizacao/bureaus-consulta" className="font-medium underline">
            Voltar a lista
          </Link>
        </Alert>
      )}
    </div>
  );
}
